import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  LinearProgress
} from '@mui/material';
import WifiIcon from '@mui/icons-material/Wifi';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import DevicesIcon from '@mui/icons-material/Devices';
import SpeedIcon from '@mui/icons-material/Speed';
import SecurityIcon from '@mui/icons-material/Security';
import * as d3 from 'd3';
import { getAPByName, generateWifiMockData } from '../../data/wifiMockData';

const WirelessAccessPointEntityDrawer = ({ entityDetails, chartRef }) => {
  const [selectedAP, setSelectedAP] = useState(null);
  const [allAPsData, setAllAPsData] = useState(null);

  useEffect(() => {
    // Generate all APs data
    const apsData = generateWifiMockData();
    setAllAPsData(apsData);

    let foundAP = null;

    // Find the specific AP based on entityDetails name
    if (entityDetails && entityDetails.name) {
      foundAP = apsData.accessPoints.find(ap => ap.name === entityDetails.name);
      
      // If not found by exact name, try AP ID from keys as fallback
      if (!foundAP && entityDetails.keys) {
        const apIdKey = entityDetails.keys.find(key => key.keyName === 'ap_id');
        if (apIdKey) {
          foundAP = getAPByName(apIdKey.value);
        }
      }
    }
    
    // Fallback to first AP if no specific AP found
    if (!foundAP && apsData.accessPoints.length > 0) {
      foundAP = apsData.accessPoints[0];
    }

    setSelectedAP(foundAP);
  }, [entityDetails]);

  const renderNetworkLineCharts = useCallback(() => {
    if (!selectedAP || !chartRef.current) return;

    // Convert 24h data to the expected format for the chart
    const timeSeriesData = selectedAP.timeSeries24h.map(item => ({
      time: item.hour.toString().padStart(2, '0') + ':00',
      connectedDevices: item.connectedDevices,
      bandwidth: item.bandwidthUtil
    }));

    // Clear previous chart
    d3.select(chartRef.current).selectAll('*').remove();

    const width = 400;
    const height = 300;
    const margin = { top: 20, right: 20, bottom: 60, left: 60 };
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    const svg = d3.select(chartRef.current)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    const data = timeSeriesData;

    // Scales
    const xScale = d3.scaleBand()
      .domain(data.map(d => d.time))
      .range([0, chartWidth])
      .padding(0.1);

    const yScaleDevices = d3.scaleLinear()
      .domain([0, d3.max(data, d => d.connectedDevices)])
      .range([chartHeight, 0]);

    const yScaleBandwidth = d3.scaleLinear()
      .domain([0, 100])
      .range([chartHeight, 0]);

    // Lines
    const lineDevices = d3.line()
      .x(d => xScale(d.time) + xScale.bandwidth() / 2)
      .y(d => yScaleDevices(d.connectedDevices));

    const lineBandwidth = d3.line()
      .x(d => xScale(d.time) + xScale.bandwidth() / 2)
      .y(d => yScaleBandwidth(d.bandwidth));

    // Axes
    svg.append('g')
      .attr('transform', `translate(0, ${chartHeight})`)
      .call(d3.axisBottom(xScale))
      .selectAll('text')
      .style('text-anchor', 'end')
      .attr('dx', '-.8em')
      .attr('dy', '.15em')
      .attr('transform', 'rotate(-45)')
      .style('font-size', '10px');

    svg.append('g')
      .call(d3.axisLeft(yScaleDevices))
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', -40)
      .attr('x', -chartHeight / 2)
      .attr('text-anchor', 'middle')
      .text('Connected Devices')
      .style('font-size', '12px');

    // Lines
    svg.append('path')
      .datum(data)
      .attr('fill', 'none')
      .attr('stroke', '#4CAF50')
      .attr('stroke-width', 3)
      .attr('d', lineDevices);

    svg.append('path')
      .datum(data)
      .attr('fill', 'none')
      .attr('stroke', '#FF9800')
      .attr('stroke-width', 3)
      .attr('d', lineBandwidth);

    // Add dots for devices
    svg.selectAll('.device-dot')
      .data(data)
      .enter()
      .append('circle')
      .attr('class', 'device-dot')
      .attr('cx', d => xScale(d.time) + xScale.bandwidth() / 2)
      .attr('cy', d => yScaleDevices(d.connectedDevices))
      .attr('r', 4)
      .attr('fill', '#4CAF50')
      .style('cursor', 'pointer')
      .on('mouseover', function(event, d) {
        d3.select(this).attr('r', 6);
        const tooltip = d3.select(chartRef.current)
          .append('div')
          .attr('class', 'tooltip')
          .style('opacity', 0)
          .style('position', 'absolute')
          .style('background', 'rgba(0, 0, 0, 0.8)')
          .style('color', 'white')
          .style('padding', '8px')
          .style('border-radius', '4px')
          .style('font-size', '12px')
          .style('pointer-events', 'none');
        tooltip.transition().duration(200).style('opacity', 0.9);
        tooltip.html(`
          <strong>Time: ${d.time}</strong><br/>
          Connected Devices: ${d.connectedDevices}<br/>
          Bandwidth: ${d.bandwidth}%
        `)
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 28) + 'px');
      })
      .on('mouseout', function() {
        d3.select(this).attr('r', 4);
        d3.select(chartRef.current).selectAll('.tooltip').remove();
      });

    // Add dots for bandwidth
    svg.selectAll('.bandwidth-dot')
      .data(data)
      .enter()
      .append('circle')
      .attr('class', 'bandwidth-dot')
      .attr('cx', d => xScale(d.time) + xScale.bandwidth() / 2)
      .attr('cy', d => yScaleBandwidth(d.bandwidth))
      .attr('r', 4)
      .attr('fill', '#FF9800')
      .style('cursor', 'pointer')
      .on('mouseover', function(event, d) {
        d3.select(this).attr('r', 6);
        
        const tooltip = d3.select(chartRef.current)
          .append('div')
          .attr('class', 'tooltip')
          .style('opacity', 0)
          .style('position', 'absolute')
          .style('background', 'rgba(0, 0, 0, 0.8)')
          .style('color', 'white')
          .style('padding', '8px')
          .style('border-radius', '4px')
          .style('font-size', '12px')
          .style('pointer-events', 'none');

        tooltip.transition()
          .duration(200)
          .style('opacity', 0.9);
        
        tooltip.html(`
          <strong>Time: ${d.time}</strong><br/>
          Connected Devices: ${d.connectedDevices}<br/>
          Bandwidth: ${d.bandwidth}%
        `)
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 28) + 'px');
      })
      .on('mouseout', function() {
        d3.select(this).attr('r', 4);
        d3.select(chartRef.current).selectAll('.tooltip').remove();
      });

    // Title
    svg.append('text')
      .attr('x', chartWidth / 2)
      .attr('y', -5)
      .attr('text-anchor', 'middle')
      .style('font-size', '14px')
      .style('font-weight', 'bold')
      .text('Network Performance - Last 24 Hours');
  }, [selectedAP]);

  useEffect(() => {
    if (chartRef.current && selectedAP && selectedAP.timeSeries24h.length > 0) {
      renderNetworkLineCharts();
    }
  }, [selectedAP, renderNetworkLineCharts]);

  // Show loading state if no AP data
  if (!selectedAP || !allAPsData) {
    return (
      <Box sx={{ p: 2 }}>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 1 }}>
          Loading WiFi access point data...
        </Typography>
      </Box>
    );
  }

  // Helper function to get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Inactive': return 'error';
      case 'Active': return 'success';
      default: return 'primary';
    }
  };

  const getHealthColor = (health) => {
    switch (health) {
      case 'Excellent': return 'success';
      case 'Good': return 'primary';
      case 'Fair': return 'warning';
      case 'Poor': return 'error';
      default: return 'primary';
    }
  };

  // Generate incidents based on AP health and status
  const incidents = [];
  if (selectedAP.status === 'Inactive') {
    incidents.push({
      id: 'INC-001',
      title: 'Access Point Offline',
      severity: 'High',
      status: 'Open',
      detectedAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
      description: `${selectedAP.name} is not responding to network requests.`
    });
  }
  if (selectedAP.health === 'Poor') {
    incidents.push({
      id: 'INC-002',
      title: 'Poor Network Performance',
      severity: 'Medium',
      status: 'Open',
      detectedAt: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
      description: `Signal strength or bandwidth issues detected on ${selectedAP.name}.`
    });
  }
  if (selectedAP.metrics.bandwidthUtilization > 90) {
    incidents.push({
      id: 'INC-003',
      title: 'High Bandwidth Utilization',
      severity: 'Medium',
      status: 'Acknowledged',
      detectedAt: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
      description: `Bandwidth utilization is ${selectedAP.metrics.bandwidthUtilization}% on ${selectedAP.name}.`
    });
  }

  return (
    <>
      {/* Wireless Access Points Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Box display="flex" alignItems="center">
              <WifiIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Box>
                <Typography variant="h6">
                  {selectedAP.name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {selectedAP.location}
                </Typography>
              </Box>
            </Box>
            <Box display="flex" gap={1}>
              <Chip 
                label={selectedAP.status} 
                color={getStatusColor(selectedAP.status)}
                variant="outlined" 
              />
              <Chip 
                label={selectedAP.health} 
                color={getHealthColor(selectedAP.health)}
                variant="outlined" 
              />
            </Box>
          </Box>

          {/* Status Alert */}
          {(selectedAP.status === 'Inactive' || selectedAP.health === 'Poor') && (
            <Alert severity={selectedAP.status === 'Inactive' ? 'error' : 'warning'} sx={{ mb: 2 }}>
              {selectedAP.status === 'Inactive' && 'Access point is offline and not responding to network requests.'}
              {selectedAP.health === 'Poor' && 'Network performance issues detected. Signal strength or bandwidth may be affected.'}
            </Alert>
          )}
          
          {/* Network Metrics Summary */}
          <Divider sx={{ my: 2 }} />
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Network Performance Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <Box textAlign="center">
                  <Typography variant="h6" color="primary">
                    {selectedAP.metrics.connectedDevices}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Connected Devices
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Box textAlign="center">
                  <Typography 
                    variant="h6" 
                    color={selectedAP.metrics.signalStrength > 80 ? 'success.main' : 
                           selectedAP.metrics.signalStrength > 60 ? 'warning.main' : 'error.main'}
                  >
                    {selectedAP.metrics.signalStrength}%
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Signal Strength
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Box textAlign="center">
                  <Typography 
                    variant="h6" 
                    color={selectedAP.metrics.bandwidthUtilization > 70 ? 'error.main' : 
                           selectedAP.metrics.bandwidthUtilization > 50 ? 'warning.main' : 'success.main'}
                  >
                    {selectedAP.metrics.bandwidthUtilization}%
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Bandwidth Usage
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Box textAlign="center">
                  <Typography variant="h6" color="info.main">
                    {selectedAP.metrics.uptime}h
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Uptime
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* Access Point Details */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            Access Point Details
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <WifiIcon />
              </ListItemIcon>
              <ListItemText 
                primary="SSIDs" 
                secondary={selectedAP.ssids.join(', ')} 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <SecurityIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Security" 
                secondary={`${selectedAP.security.encryption} (${selectedAP.security.authMethod})`} 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <DevicesIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Device Capacity" 
                secondary={`${selectedAP.metrics.connectedDevices}/${selectedAP.metrics.maxDevices} devices`} 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <SpeedIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Data Transferred Today" 
                secondary={`${selectedAP.metrics.dataTransferred} MB`} 
              />
            </ListItem>
          </List>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle2" gutterBottom>
            System Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box textAlign="center">
                <Typography variant="h6" color="primary">
                  {allAPsData.summary.totalAPs}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Total APs
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box textAlign="center">
                <Typography variant="h6" color="success.main">
                  {allAPsData.summary.activeAPs}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Active APs
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Network Performance Charts */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
            <SignalCellularAltIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">
              Network Performance Trends
            </Typography>
          </Box>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            24-hour performance metrics showing connected devices and bandwidth utilization patterns.
          </Typography>
          <Box 
            ref={chartRef}
            sx={{ 
              display: 'flex', 
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '320px'
            }}
          />
        </CardContent>
      </Card>

      {/* Incidents */}
      {incidents.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
              <ReportProblemIcon sx={{ mr: 1, color: 'warning.main' }} />
              <Typography variant="h6">
                Active Incidents
              </Typography>
            </Box>
            <List dense>
              {incidents.map((incident) => (
                <ListItem key={incident.id} divider>
                  <ListItemIcon>
                    <ReportProblemIcon 
                      color={incident.severity === 'High' ? 'error' : 'warning'} 
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle2">
                          {incident.title}
                        </Typography>
                        <Chip 
                          label={incident.severity} 
                          size="small" 
                          color={incident.severity === 'High' ? 'error' : 'warning'}
                          variant="outlined" 
                        />
                        <Chip 
                          label={incident.status} 
                          size="small" 
                          color="info"
                          variant="outlined" 
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          {incident.description}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Detected: {new Date(incident.detectedAt).toLocaleString()}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default WirelessAccessPointEntityDrawer;
